import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';
import { useOptionalWorkspace } from '@/app/providers/workspace-provider';
import { useTheme } from '@/app/providers/theme-provider';
import { resolveSlothState, type SlothState } from '@/components/character/sloth-states';
import { nowLocalDateTime } from '@/lib/clock';
import { differenceInLocalDays, toLocalDate, todayLocalDate } from '@/lib/dates/local-date';

/** 資料異動後幾天沒備份就開始提醒（spec 20.7）。 */
const BACKUP_REMINDER_DAYS = 7;

export interface AssistantState {
  state: SlothState;
  tip: string;
  reminderCount: number;
}

/**
 * 樹懶只根據現有資料顯示規則式提示，不宣稱使用 AI（spec 8.2）。
 * 提示不責備、不催促、不製造焦慮。
 */
export function useAssistantState(): AssistantState {
  const workspace = useOptionalWorkspace();
  const { resolvedTheme } = useTheme();
  const db = workspace?.db;

  const counts = useLiveQuery(async () => {
    if (!db) return null;
    const now = nowLocalDateTime();
    const today = todayLocalDate();

    const [tasks, reminders, history] = await Promise.all([
      db.tasks.toArray(),
      db.supervisorReminders.toArray(),
      db.backupHistory.where('scope').equals('full').toArray(),
    ]);

    const live = tasks.filter((task) => task.deletedAt === null);
    const liveIds = new Set(live.map((task) => task.id));

    const dueReminders = reminders.filter(
      (reminder) =>
        (reminder.state === 'pending' || reminder.state === 'snoozed') &&
        liveIds.has(reminder.taskId) &&
        (reminder.snoozedUntilLocal ?? reminder.remindAtLocal) <= now,
    );

    const open = live.filter((task) => task.completedAt === null && task.cancelledAt === null);
    const waiting = open.filter((task) => task.status === 'waiting');
    const overdue = open.filter(
      (task) =>
        task.status !== 'waiting' &&
        task.dueDate !== null &&
        differenceInLocalDays(task.dueDate, today) < 0,
    );
    const dueToday = open.filter(
      (task) => task.dueDate !== null && differenceInLocalDays(task.dueDate, today) === 0,
    );

    const lastFull = history.sort((a, b) => a.exportedAt.localeCompare(b.exportedAt)).at(-1);
    const backupOverdue = workspace?.settings?.lastDataMutationAt
      ? !lastFull ||
        differenceInLocalDays(
          toLocalDate(new Date(workspace.settings.lastDataMutationAt)),
          toLocalDate(new Date(lastFull.exportedAt)),
        ) >= BACKUP_REMINDER_DAYS
      : false;

    return {
      reminderCount: dueReminders.length,
      waitingCount: waiting.length,
      overdueCount: overdue.length,
      dueTodayCount: dueToday.length,
      backupOverdue,
    };
  }, [db, workspace?.settings?.lastDataMutationAt]);

  return useMemo(() => {
    const isSeeded = workspace?.isSeeded ?? false;
    const saving = workspace?.saveState === 'saving';

    const state = resolveSlothState({
      isSeeded,
      saving,
      reminderCount: counts?.reminderCount ?? 0,
      waitingCount: counts?.waitingCount ?? 0,
      stuckCount: 0,
      backupOverdue: counts?.backupOverdue ?? false,
      theme: resolvedTheme,
    });

    return {
      state,
      tip: buildTip(state, counts),
      reminderCount: counts?.reminderCount ?? 0,
    };
  }, [workspace?.isSeeded, workspace?.saveState, counts, resolvedTheme]);
}

interface TipCounts {
  reminderCount: number;
  waitingCount: number;
  overdueCount: number;
  dueTodayCount: number;
  backupOverdue: boolean;
}

function buildTip(state: SlothState, counts: TipCounts | null | undefined): string {
  if (state === 'empty') {
    return '工作台還是空的，可以從備份匯入或建立全新的工作台。';
  }
  if (state === 'working') return '正在把剛才的變更保存起來。';

  if (counts) {
    if (counts.reminderCount > 0) {
      return `今天先提醒主管 ${counts.reminderCount} 件事。`;
    }
    if (counts.overdueCount > 0) {
      return `有 ${counts.overdueCount} 件事過了原本的日子，要不要重新排一下？`;
    }
    if (counts.dueTodayCount > 0) {
      return `今天有 ${counts.dueTodayCount} 件事要處理。`;
    }
    if (counts.waitingCount > 0) {
      return `有 ${counts.waitingCount} 件事在等對方回覆。`;
    }
    if (counts.backupOverdue) {
      return '距離上次完整備份有一段時間了，有空時匯出一份吧。';
    }
  }

  if (state === 'night') return '夜深了，這裡的燈幫你留著。';
  return '今天還很安靜，需要記下什麼嗎？';
}
